import { useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DeepPartial } from "@apollo/client/utilities";
import { LanguageProxy } from "@me-team/host/main/graphql/types";
import { TFunction } from "i18next";

type Option = {
    value: number;
    label: string;
};

export type MenuLanguagesFormValues = {
    languages: number[];
    primaryLanguage: number | null;
};

type Props = {
    languages: DeepPartial<LanguageProxy[]>;
    onSubmit: (values: MenuLanguagesFormValues) => void;
    t: TFunction;
};

const initialValues: MenuLanguagesFormValues = {
    languages: [],
    primaryLanguage: null,
};

export const useMenuLanguagesForm = ({ languages, onSubmit, t }: Props) => {
    const languageOptions: Option[] = useMemo(() => {
        return (languages ?? [])
            .filter((language) => language?.id != null)
            .map((language) => ({
                value: Number(language.id),
                label: String(language.name ?? ""),
            }));
    }, [languages]);

    const validationSchema = useMemo(() => {
        const requiredMessage = t("validation.required");

        return Yup.object({
            languages: Yup.array().of(Yup.number().required()).min(1, requiredMessage).required(requiredMessage),

            primaryLanguage: Yup.number()
                .nullable()
                .required(requiredMessage)
                .test("primary-in-languages", requiredMessage, function (value) {
                    const { languages } = this.parent as MenuLanguagesFormValues;

                    if (value == null) return false;

                    return languages.includes(value);
                }),
        });
    }, [t]);

    const formik = useFormik<MenuLanguagesFormValues>({
        initialValues,
        validationSchema,
        onSubmit,
    });

    const primaryLanguageOptions = useMemo(() => {
        const selectedLanguageIds = formik.values.languages ?? [];

        return languageOptions.filter((option) => selectedLanguageIds.includes(option.value));
    }, [languageOptions, formik.values.languages]);

    const primaryLanguageValue = useMemo(() => {
        return primaryLanguageOptions.find((option) => option.value === formik.values.primaryLanguage) ?? null;
    }, [primaryLanguageOptions, formik.values.primaryLanguage]);

    const handleLanguagesChange = (selected: Option[] | null) => {
        const ids = (selected ?? []).map((option) => option.value);

        formik.setFieldValue("languages", ids);

        if (formik.values.primaryLanguage != null && !ids.includes(formik.values.primaryLanguage)) {
            formik.setFieldValue("primaryLanguage", null);
        }
    };

    const handlePrimaryLanguageChange = (option: Option | null) => {
        formik.setFieldValue("primaryLanguage", option?.value ?? null);
    };

    return {
        formik,
        languageOptions,
        primaryLanguageOptions,
        primaryLanguageValue,
        handleLanguagesChange,
        handlePrimaryLanguageChange,
    };
};
